import { RefObject, useLayoutEffect, useRef } from 'react';
import usePrefersReducedMotion from '@app/hooks/usePrefersReducedMotion';
import { HOME_WIDGET_TRANSITION_MS } from './widgetLayoutConstants';

const FLIP_TRANSITION = `transform ${HOME_WIDGET_TRANSITION_MS}ms cubic-bezier(0.2, 0, 0, 1)`;

const useFlipLayoutAnimation = (
  containerRef: RefObject<HTMLElement | null>,
  animationKey: string,
  isAnimating: boolean
) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const positionsRef = useRef<Map<string, DOMRect>>(new Map());
  const isFirstLayoutRef = useRef(true);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || prefersReducedMotion || !isAnimating) {
      if (container && isFirstLayoutRef.current) {
        const elements = container.querySelectorAll<HTMLElement>(
          '[data-layout-animated][data-widget-id]'
        );
        const nextPositions = new Map<string, DOMRect>();
        elements.forEach((element) => {
          const widgetId = element.getAttribute('data-widget-id');
          if (widgetId) {
            nextPositions.set(widgetId, element.getBoundingClientRect());
          }
        });
        positionsRef.current = nextPositions;
        isFirstLayoutRef.current = false;
      }
      return;
    }

    const elements = container.querySelectorAll<HTMLElement>(
      '[data-layout-animated][data-widget-id]:not([data-layout-exiting="true"])'
    );
    const nextPositions = new Map<string, DOMRect>();

    elements.forEach((element) => {
      const widgetId = element.getAttribute('data-widget-id');
      if (!widgetId) {
        return;
      }

      const nextRect = element.getBoundingClientRect();
      nextPositions.set(widgetId, nextRect);

      if (isFirstLayoutRef.current) {
        return;
      }

      const previousRect = positionsRef.current.get(widgetId);
      if (!previousRect) {
        return;
      }

      const deltaX = previousRect.left - nextRect.left;
      const deltaY = previousRect.top - nextRect.top;

      if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) {
        return;
      }

      element.style.willChange = 'transform';
      element.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
      element.style.transition = 'none';

      requestAnimationFrame(() => {
        element.style.transition = FLIP_TRANSITION;
        element.style.transform = '';

        const handleTransitionEnd = () => {
          element.style.willChange = '';
          element.style.transition = '';
          element.removeEventListener('transitionend', handleTransitionEnd);
        };

        element.addEventListener('transitionend', handleTransitionEnd);
      });
    });

    positionsRef.current = nextPositions;
    isFirstLayoutRef.current = false;
  }, [animationKey, containerRef, prefersReducedMotion, isAnimating]);
};

export default useFlipLayoutAnimation;
